import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Loader2, MailCheck, Play, Pencil, ShieldAlert, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDERS = "{client_name} · {invoice_number} · {amount} · {days_overdue} · {business_name}";

/**
 * Phase F.1 v2 — AR Auto-Reminders admin card (HYBRID).
 *
 * Master toggle defaults OFF. When admin flips it ON the **template editor**
 * pops up automatically so they can review/edit the gentle / firm / final
 * messages before anything sends. An Edit button next to the toggle re-opens
 * the editor anytime. Toggling OFF→ON also re-opens it (keeping the saved
 * messages so admin can just confirm).
 *
 * Hybrid: GENTLE tone is auto-sent at cadence; FIRM and FINAL drafts land in
 * the admin Pending Review queue rendered below the card.
 */
export default function ArAutoRemindersCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTone, setEditorTone] = useState("gentle");
  const [editorBuffer, setEditorBuffer] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/ar-reminders/settings`),
        axios.get(`${API}/ar-reminders/pending`).catch(() => ({ data: { pending: [] } })),
      ]);
      setSettings(s.data);
      setPending(p.data?.pending || []);
    } catch (e) { /* admin-only */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEditor = (current = null) => {
    const src = current?.templates || settings?.templates || {};
    setEditorBuffer({
      gentle: { ...(src.gentle || { subject: "", body: "" }) },
      firm: { ...(src.firm || { subject: "", body: "" }) },
      final: { ...(src.final || { subject: "", body: "" }) },
    });
    setEditorTone("gentle");
    setEditorOpen(true);
  };

  const toggleEnabled = async (next) => {
    if (!settings) return;
    if (next && !settings.templates_initialized) {
      // First time turning ON — force the editor so admin reviews messages first.
      openEditor(settings);
      // We optimistically set enabled=true after templates are saved.
      // Persist enabled=true alongside templates save.
      setSettings({ ...settings, enabled: true });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/ar-reminders/settings`, {
        enabled: next,
        min_days_overdue: settings.min_days_overdue,
        cadence_days: settings.cadence_days,
        max_reminders_per_invoice: settings.max_reminders_per_invoice,
      });
      setSettings(res.data);
      if (next) toast.success("AR Auto-Reminders enabled");
      else toast.info("AR Auto-Reminders disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const saveField = async (patch) => {
    if (!settings) return;
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const res = await axios.put(`${API}/ar-reminders/settings`, {
        enabled: next.enabled,
        min_days_overdue: next.min_days_overdue,
        cadence_days: next.cadence_days,
        max_reminders_per_invoice: next.max_reminders_per_invoice,
      });
      setSettings(res.data);
    } catch (e) { toast.error("Failed to save"); }
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/ar-reminders/settings/templates`, { templates: editorBuffer });
      // After saving templates, persist the enabled state too (so first-time toggle goes through)
      await axios.put(`${API}/ar-reminders/settings`, {
        enabled: true,
        min_days_overdue: res.data.min_days_overdue,
        cadence_days: res.data.cadence_days,
        max_reminders_per_invoice: res.data.max_reminders_per_invoice,
      });
      const refreshed = await axios.get(`${API}/ar-reminders/settings`);
      setSettings(refreshed.data);
      setEditorOpen(false);
      toast.success("Templates saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save templates");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/ar-reminders/run`);
      const r = res.data || {};
      if (r.skipped_reason === "disabled") {
        toast.info("AR Auto-Reminders are off — turn them on to run.");
      } else {
        toast.success(
          `Considered ${r.considered || 0} · Sent ${r.sent || 0} · Queued ${r.queued_for_review || 0} · Skipped ${r.skipped || 0} · Failed ${r.failed || 0}`
        );
      }
      fetchAll();
    } catch (e) { toast.error("Run failed"); }
    finally { setRunning(false); }
  };

  const approveLog = async (id) => {
    try { await axios.post(`${API}/ar-reminders/logs/${id}/approve`); toast.success("Sent"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const dismissLog = async (id) => {
    try { await axios.post(`${API}/ar-reminders/logs/${id}/dismiss`); toast.success("Dismissed"); fetchAll(); }
    catch (e) { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading AR auto-reminders…
      </CardContent></Card>
    );
  }
  if (!settings) return null;

  return (
    <>
      <Card data-testid="ar-auto-reminders-card" className="border-emerald-200">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MailCheck className="h-5 w-5 text-emerald-600" />
                AR Auto-Reminders
                <Badge variant="outline" className={settings.enabled ? "border-emerald-300 text-emerald-700" : "text-muted-foreground"}>
                  {settings.enabled ? "ON" : "OFF"}
                </Badge>
              </CardTitle>
              <CardDescription>
                When <strong>ON</strong>: invoices past their due date are auto-followed-up using the templates below.
                Gentle reminders are sent automatically; Firm and Final tones land in your Pending Review queue for one-click approval.
                Assumes invoices are unpaid until you mark them paid. Per-invoice override is available on each invoice row.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={toggleEnabled}
                disabled={saving}
                data-testid="ar-reminders-enabled-switch"
              />
              {settings.enabled && (
                <Button size="sm" variant="outline" onClick={() => openEditor(settings)} data-testid="ar-reminders-edit-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit templates
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="ar-reminders-run-now-btn">
                {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                Run Now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Min days overdue</Label>
              <Input type="number" min={0} value={settings.min_days_overdue}
                     onChange={(e) => saveField({ min_days_overdue: Number(e.target.value) })}
                     disabled={!settings.enabled || saving}
                     data-testid="ar-reminders-min-days-input" />
              <p className="text-xs text-muted-foreground mt-1">Wait this long after due-date before the first nudge.</p>
            </div>
            <div>
              <Label className="text-xs">Cadence (days)</Label>
              <Input type="number" min={1} value={settings.cadence_days}
                     onChange={(e) => saveField({ cadence_days: Number(e.target.value) })}
                     disabled={!settings.enabled || saving}
                     data-testid="ar-reminders-cadence-input" />
              <p className="text-xs text-muted-foreground mt-1">Days between follow-ups on the same invoice.</p>
            </div>
            <div>
              <Label className="text-xs">Max per invoice</Label>
              <Input type="number" min={1} value={settings.max_reminders_per_invoice}
                     onChange={(e) => saveField({ max_reminders_per_invoice: Number(e.target.value) })}
                     disabled={!settings.enabled || saving}
                     data-testid="ar-reminders-max-input" />
              <p className="text-xs text-muted-foreground mt-1">Total follow-ups before we stop chasing.</p>
            </div>
          </div>

          {pending.length > 0 && (
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                <p className="text-sm font-medium">Pending Review ({pending.length})</p>
              </div>
              <div className="border rounded-lg divide-y" data-testid="ar-reminders-pending-list">
                {pending.map((lg) => (
                  <div key={lg.id} className="p-3 text-sm" data-testid={`ar-rem-pending-${lg.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={
                          lg.tone === "firm" ? "border-amber-300 text-amber-700" : "border-rose-300 text-rose-700"
                        }>{lg.tone}</Badge>
                        <span className="font-medium">{lg.subject || "(no subject)"}</span>
                        <span className="text-xs text-muted-foreground">{lg.days_overdue}d overdue</span>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => approveLog(lg.id)} data-testid={`ar-rem-approve-${lg.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Approve & send
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismissLog(lg.id)} data-testid={`ar-rem-dismiss-${lg.id}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                        </Button>
                      </div>
                    </div>
                    {lg.body_html && (
                      <div className="mt-2 text-xs text-muted-foreground border-l-2 pl-2 max-h-32 overflow-y-auto"
                           dangerouslySetInnerHTML={{ __html: lg.body_html }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {settings.last_run_at && (
            <p className="text-xs text-muted-foreground">Last sweep: {new Date(settings.last_run_at).toLocaleString()}</p>
          )}
        </CardContent>
      </Card>

      {/* Template editor — opens on first toggle and via Edit button */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl" data-testid="ar-reminders-editor-dialog">
          <DialogHeader>
            <DialogTitle>AR Reminder Templates</DialogTitle>
            <DialogDescription>
              Edit the email each tone sends. Placeholders: <code>{PLACEHOLDERS}</code>
              <br />Gentle is auto-sent. Firm and Final require your approval in the Pending Review queue.
            </DialogDescription>
          </DialogHeader>
          <Tabs value={editorTone} onValueChange={setEditorTone}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="gentle" data-testid="ar-rem-tab-gentle">Gentle (auto)</TabsTrigger>
              <TabsTrigger value="firm" data-testid="ar-rem-tab-firm">Firm (review)</TabsTrigger>
              <TabsTrigger value="final" data-testid="ar-rem-tab-final">Final (review)</TabsTrigger>
            </TabsList>
            {["gentle", "firm", "final"].map((tone) => (
              <TabsContent key={tone} value={tone} className="space-y-3">
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input
                    value={editorBuffer[tone]?.subject || ""}
                    onChange={(e) => setEditorBuffer({ ...editorBuffer, [tone]: { ...editorBuffer[tone], subject: e.target.value } })}
                    data-testid={`ar-rem-editor-${tone}-subject`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Body</Label>
                  <Textarea
                    rows={10}
                    value={editorBuffer[tone]?.body || ""}
                    onChange={(e) => setEditorBuffer({ ...editorBuffer, [tone]: { ...editorBuffer[tone], body: e.target.value } })}
                    data-testid={`ar-rem-editor-${tone}-body`}
                  />
                </div>
              </TabsContent>
            ))}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplates} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ar-rem-editor-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save & turn on
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
